import { useState, type ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { asList, asRecord, hasValue, erased } from "./AllMattersV2Fields";
import {
  KeyValues,
  PageLink,
  PartyName,
  PartyTable,
  RankBadge,
  Section,
  Tag,
  rightTone,
  text,
  yen,
} from "./AllMattersV2Parts";
import { CurrentJointSecurities } from "./AllMattersV2JointSecurity";

const securityFields = (entry: unknown): Record<string, unknown> => {
  const item = asRecord(entry);
  return {
    順位番号: item.順位番号,
    登記の目的: item.登記の目的 ?? item.種類,
    種類: item.種類,
    // Unknown current values stay unknown; older amounts never replace them.
    担保権者:
      "現在の担保権者" in item
        ? item.現在の担保権者
        : item.担保権者 ?? item.権利者,
    金額:
      "現在の債権額極度額" in item
        ? item.現在の債権額極度額 ?? "不明"
        : item.債権額 ?? item.極度額,
    債務者: "現在の債務者" in item ? item.現在の債務者 : item.債務者,
    // Schema 6.2: the same people in the same order, with their addresses.
    担保権者明細: item.現在の担保権者明細,
    債務者明細: item.現在の債務者明細,
    共同担保物件: item.共同担保物件,
    現在の共同担保明細: item.現在の共同担保明細,
    仮登記: item.仮登記,
    元本確定: item.元本確定,
  };
};

/**
 * Current holders or debtors with their addresses (Schema 6.2). Older results
 * only carry names, which stay one comma-separated line. A debtor's label is
 * shown only when it is not plain 債務者 (連帯債務者・指定債務者).
 */
const currentParties = (
  details: unknown,
  names: unknown,
  plainLabel?: string
): ReactNode => {
  const parties = asList(details);
  if (!parties.length) return text(names);
  return (
    <Stack spacing={0.75}>
      {parties.map((party, index) => {
        const label = text(asRecord(party).ラベル);
        return (
          <Stack key={index} direction="row" gap={1} alignItems="baseline">
            {plainLabel && label && label !== plainLabel && (
              <Tag label={label} />
            )}
            <PartyName party={party} />
          </Stack>
        );
      })}
    </Stack>
  );
};

/** Older results said 高 for what Schema 6 calls 確定. */
const settled = (certainty: unknown): boolean =>
  certainty === "確定" || certainty === "高";

const severityOrder: Record<string, number> = { 阻害: 0, 要確認: 1 };

const FALLBACK_REASON = "登記の PDF と照合してください。";

interface ReviewReason {
  reason: string;
  pages: number[];
  targets: string[];
}

/** An issue with the rows it affects. */
interface ReviewIssue {
  issue: unknown;
  targets?: string[];
}

/**
 * Blocking and needs-check issues as one line per distinct reason, the
 * blocking ones first. 参考 issues do not lower the certainty and stay hidden.
 */
const reviewReasons = (issues: ReviewIssue[]): ReviewReason[] => {
  const byReason = new Map<
    string,
    { pages: Set<number>; targets: Set<string> }
  >();
  issues
    .map(({ issue, targets }) => ({ issue: asRecord(issue), targets }))
    .filter(({ issue }) => issue.重要度 !== "参考")
    .map((entry, index) => ({ ...entry, index }))
    .sort(
      (a, b) =>
        (severityOrder[String(a.issue.重要度)] ?? 2) -
          (severityOrder[String(b.issue.重要度)] ?? 2) || a.index - b.index
    )
    .forEach(({ issue, targets }) => {
      const reason =
        typeof issue.理由 === "string" && issue.理由
          ? issue.理由
          : FALLBACK_REASON;
      const entry = byReason.get(reason) ?? {
        pages: new Set<number>(),
        targets: new Set<string>(),
      };
      const page = issue.ページ;
      if (typeof page === "number" && Number.isInteger(page) && page > 0)
        entry.pages.add(page);
      targets?.forEach((target) => entry.targets.add(target));
      byReason.set(reason, entry);
    });
  return [...byReason].map(([reason, { pages, targets }]) => ({
    reason,
    pages: [...pages].sort((a, b) => a - b),
    targets: [...targets],
  }));
};

const FOLDED_REASONS = 3;

const ReviewReasons = ({
  issues,
  rowCount,
  onShowPage,
}: {
  issues: ReviewIssue[];
  /** Rows in the table; a reason names its rows only when not all of them. */
  rowCount?: number;
  onShowPage?: (page: number) => void;
}): JSX.Element => {
  const [expanded, setExpanded] = useState(false);
  const reasons = reviewReasons(issues);
  const shown = expanded ? reasons : reasons.slice(0, FOLDED_REASONS);
  return (
    <Box
      sx={{
        px: 1.5,
        py: 1,
        border: 1,
        borderColor: "warning.light",
        borderRadius: 1,
      }}
    >
      <Typography variant="body2" fontWeight={600}>
        確認が必要な理由
      </Typography>
      {reasons.length ? (
        <Stack component="ul" spacing={0.5} sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
          {shown.map(({ reason, pages, targets }) => (
            <Box component="li" key={reason}>
              <Stack
                direction="row"
                alignItems="center"
                gap={0.5}
                flexWrap="wrap"
              >
                <Box sx={{ flex: "1 1 12em", minWidth: 0 }}>
                  <Typography variant="body2">{reason}</Typography>
                  {targets.length > 0 && targets.length < (rowCount ?? 0) && (
                    <Typography variant="caption" color="text.secondary">
                      対象: {targets.join("、")}
                    </Typography>
                  )}
                </Box>
                {onShowPage &&
                  pages.map((page) => (
                    <PageLink key={page} page={page} onShowPage={onShowPage} />
                  ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      ) : (
        <Typography variant="body2" mt={0.5}>
          {FALLBACK_REASON}
        </Typography>
      )}
      {reasons.length > FOLDED_REASONS && (
        <Button
          size="small"
          sx={{ minHeight: 44 }}
          onClick={() => {
            setExpanded(!expanded);
          }}
        >
          {expanded
            ? "理由を閉じる"
            : `ほかの理由 ${reasons.length - FOLDED_REASONS}件を表示`}
        </Button>
      )}
    </Box>
  );
};

/** Schema 6.3: a row's own certainty and the view issues that affect it. */
interface RowReview {
  status: string;
  unsettled: boolean;
  issues: unknown[];
}

const rowReviews = (rows: unknown[], issues: unknown[]): RowReview[] | null =>
  rows.some((row) => typeof asRecord(row).確度 === "string")
    ? rows.map((row) => {
        const item = asRecord(row);
        return {
          status: typeof item.確度 === "string" ? item.確度 : "",
          unsettled: typeof item.確度 === "string" && !settled(item.確度),
          issues: asList(item.問題番号)
            .filter((index): index is number => typeof index === "number")
            .map((index) => issues[index])
            .filter((issue) => issue != null),
        };
      })
    : null;

/** The row's issues for the reasons box; an unexplained 要確認 asks for the PDF. */
const rowIssues = (review: RowReview, target?: string): ReviewIssue[] => {
  const targets = target ? [target] : undefined;
  const issues = review.issues
    .filter((issue) => asRecord(issue).重要度 !== "参考")
    .map((issue) => ({ issue, targets }));
  return issues.length
    ? issues
    : [{ issue: { 重要度: "要確認", 理由: FALLBACK_REASON }, targets }];
};

const ReviewMark = ({ status }: { status?: string }): JSX.Element => (
  <Tag label={status === "判定不能" ? "判定不能" : "要確認"} tone="warning" />
);

/** Empty values by Schema 6.3 空の理由; 不明 stays apart from a settled none. */
const EmptyValue = ({
  label,
  note,
}: {
  label: string;
  note?: string;
}): JSX.Element => (
  <Typography variant="body2" px={0.5}>
    <Box component="span" fontWeight={700}>
      {label}
    </Box>
    {note && `（${note}）`}
  </Typography>
);

/**
 * Values come first whatever the certainty. An unsettled conclusion shows its
 * status and reasons, and an empty unsettled value reads 不明 rather than none.
 */
export const Conclusion = ({
  title,
  value,
  empty,
  emptyReasons = {},
  onShowPage,
  detailedRows = false,
  children,
}: {
  title: string;
  value: unknown;
  empty: string;
  /** Wording for 空の理由 other than 記録なし and 不明. */
  emptyReasons?: Record<string, ReactNode>;
  onShowPage?: (page: number) => void;
  detailedRows?: boolean;
  children: (values: unknown[], rows: RowReview[] | null) => JSX.Element;
}): JSX.Element => {
  const conclusion = asRecord(value);
  const values = asList(conclusion.値);
  const rows = detailedRows
    ? rowReviews(values, asList(conclusion.問題))
    : null;
  const emptyReason =
    typeof conclusion.空の理由 === "string" ? conclusion.空の理由 : null;
  const rowCertainties = values
    .map((item) => asRecord(item).確度)
    .filter(
      (status): status is string =>
        typeof status === "string" && !settled(status)
    );
  const review =
    rows == null &&
    ((typeof conclusion.確度 === "string" && !settled(conclusion.確度)) ||
      rowCertainties.length > 0);
  const unknownEmpty =
    emptyReason === "不明" ||
    ((emptyReason == null || emptyReason === "記録なし") && review);
  return (
    <Section
      title={title}
      tags={
        review ? (
          <Tag
            label={
              conclusion.確度 === "判定不能" ||
              rowCertainties.includes("判定不能")
                ? "判定不能"
                : "要確認"
            }
            tone="warning"
          />
        ) : !detailedRows && typeof conclusion.確度 === "string" ? (
          <Tag label={`確度: ${conclusion.確度}`} />
        ) : undefined
      }
    >
      {conclusion.値 == null ? (
        <Alert severity="warning">
          判定できませんでした。登記の PDF を確認してください。
        </Alert>
      ) : Array.isArray(conclusion.値) && !conclusion.値.length ? (
        unknownEmpty ? (
          <EmptyValue
            label="不明"
            note="該当する権利がないとは確定していません"
          />
        ) : emptyReason && emptyReason in emptyReasons ? (
          emptyReasons[emptyReason]
        ) : (
          <Typography variant="body2" color="text.secondary" px={0.5}>
            {empty}
          </Typography>
        )
      ) : (
        children(values, rows)
      )}
      {review && (
        <ReviewReasons
          issues={asList(conclusion.問題).map((issue) => ({ issue }))}
          onShowPage={onShowPage}
        />
      )}
    </Section>
  );
};

/** Display per-party certainty and its linked issues without discarding the value. */
export const ReviewedParties = ({
  values,
  issues,
  onShowPage,
  mapParty = (value) => value,
}: {
  values: unknown[];
  issues: unknown[];
  onShowPage: (page: number) => void;
  mapParty?: (value: unknown) => unknown;
}): JSX.Element => {
  const reviews = rowReviews(values, issues);
  return (
    <Stack spacing={1}>
      <PartyTable
        parties={values.map(mapParty)}
        extras={values.map((_, index) => {
          const review = reviews?.[index];
          return review?.unsettled ? (
            <Stack spacing={0.5}>
              <ReviewMark status={review.status} />
              <ReviewReasons
                issues={rowIssues(review)}
                onShowPage={onShowPage}
              />
            </Stack>
          ) : undefined;
        })}
      />
    </Stack>
  );
};

/** Owners not computed from 甲区 say where they come from. */
const originNotes: Partial<Record<string, string>> = {
  表題部所有者: "所有権の登記なし（表題部所有者）",
  記録外: "記録外の名義",
};

const OwnerRows = ({
  owners,
  rows,
  onShowPage,
}: {
  owners: unknown[];
  rows: RowReview[] | null;
  onShowPage: (page: number) => void;
}): JSX.Element => {
  const names = owners.map(
    (owner, index) =>
      text(asRecord(owner).所有者 ?? asRecord(owner).氏名) || `${index + 1}人目`
  );
  const unsettled = (rows ?? [])
    .map((review, index) => ({ review, index }))
    .filter(({ review }) => review.unsettled);
  return (
    <>
      <PartyTable
        parties={owners.map((owner) => {
          const item = asRecord(owner);
          return {
            ...item,
            氏名: item.所有者 ?? item.氏名,
            住所: item.所有者住所 ?? item.住所,
            持分: item.持分,
          };
        })}
        extras={owners.map((owner, index) => {
          const origin = originNotes[text(asRecord(owner).由来)];
          const marked = rows?.[index]?.unsettled === true;
          return marked || origin ? (
            <Stack direction="row" gap={0.5} flexWrap="wrap">
              {marked && <ReviewMark status={rows?.[index]?.status} />}
              {origin && <Tag label={origin} tone="info" />}
            </Stack>
          ) : null;
        })}
      />
      {unsettled.length > 0 && (
        <ReviewReasons
          issues={unsettled.flatMap(({ review, index }) =>
            rowIssues(review, names[index])
          )}
          rowCount={owners.length}
          onShowPage={onShowPage}
        />
      )}
    </>
  );
};

const SecurityCard = ({
  entry,
  review,
  jointCatalogs,
  onOpenJointCatalog,
  onShowPage,
}: {
  entry: unknown;
  /** Schema 6.3: this security's own certainty. */
  review?: RowReview;
  jointCatalogs: unknown[];
  onOpenJointCatalog?: (index: number) => void;
  onShowPage: (page: number) => void;
}): JSX.Element => {
  const item = securityFields(entry);
  const jointItems = asList(item.現在の共同担保明細);
  const purpose = text(item.登記の目的);
  const kind =
    text(item.種類) || (purpose.startsWith("根抵当権") ? "根抵当権" : "");
  const tone = rightTone(kind || (purpose.includes("抵当権") ? "抵当権" : ""));
  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        p: 1.5,
        borderLeft: 4,
        borderLeftColor:
          tone === "default" ? theme.palette.divider : theme.palette[tone].main,
      })}
    >
      <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
        {hasValue(item.順位番号) && (
          <RankBadge label={`順位 ${text(item.順位番号)}`} />
        )}
        <Typography fontWeight={700}>{purpose || "担保権"}</Typography>
        {item.仮登記 === true && <Tag label="仮登記" tone="warning" />}
        {item.元本確定 === true && <Tag label="元本確定" tone="info" />}
        {review?.unsettled && <ReviewMark status={review.status} />}
      </Stack>
      <Box mt={1}>
        <KeyValues
          items={[
            ["担保権者", currentParties(item.担保権者明細, item.担保権者)],
            [kind === "根抵当権" ? "極度額" : "債権額・極度額", yen(item.金額)],
            ["債務者", currentParties(item.債務者明細, item.債務者, "債務者")],
            [
              "共同担保物件",
              jointItems.length ? (
                <CurrentJointSecurities
                  items={jointItems}
                  catalogs={jointCatalogs}
                  onOpenCatalog={onOpenJointCatalog}
                />
              ) : asList(item.共同担保物件).length ? (
                // Older results only carry display strings: one per line.
                asList(item.共同担保物件).map(text).join("\n")
              ) : (
                text(item.共同担保物件)
              ),
            ],
          ]}
        />
      </Box>
      {review?.unsettled && (
        <Box mt={1}>
          <ReviewReasons issues={rowIssues(review)} onShowPage={onShowPage} />
        </Box>
      )}
    </Paper>
  );
};

const CountTile = ({
  label,
  count,
  note,
  onClick,
}: {
  label: string;
  /** null when the section could not be read. */
  count: number | null;
  note?: string;
  onClick?: () => void;
}): JSX.Element => (
  <ButtonBase
    onClick={onClick}
    disabled={!onClick}
    aria-label={`${label}を開く`}
    sx={{
      flex: "1 1 7.5em",
      minHeight: 44,
      display: "block",
      textAlign: "left",
      px: 1.5,
      py: 1,
      border: 1,
      borderColor: "divider",
      borderRadius: 1,
      "&:hover": { bgcolor: "action.hover" },
    }}
  >
    <Typography variant="caption" color="text.secondary" component="div">
      {label}
    </Typography>
    <Typography fontWeight={700} component="div">
      {count == null ? "—" : `${count}件`}
      {note && (
        <Typography
          component="span"
          variant="caption"
          color="text.secondary"
          ml={0.5}
        >
          {note}
        </Typography>
      )}
    </Typography>
  </ButtonBase>
);

export const AllMattersV2Overview = ({
  result,
  sections,
  onSelect,
  onOpenJointCatalog,
  onShowPage,
}: {
  result: Record<string, unknown>;
  sections: Array<[string, unknown]>;
  onSelect: (section: string) => void;
  onOpenJointCatalog?: (index: number) => void;
  onShowPage: (page: number) => void;
}): JSX.Element => {
  const views = asRecord(result.ビュー);
  const jointCatalogs = asList(asRecord(result.権利部).共同担保目録);
  const tiles = sections
    .filter(([title]) => title !== "表題部")
    .map(([title, value]) => {
      const entries = asList(value);
      const erasedCount = entries.filter(erased).length;
      // A failed section keeps its tile without claiming zero entries.
      if (!Array.isArray(value))
        return { title, count: null, note: "解析できませんでした" };
      return {
        title,
        count: entries.length,
        note: erasedCount ? `うち抹消 ${erasedCount}` : undefined,
      };
    });
  return (
    <Stack spacing={3}>
      {result.閉鎖 === true && (
        <Alert severity="warning">閉鎖された登記です。</Alert>
      )}
      {tiles.length > 0 && (
        <Stack direction="row" gap={1} flexWrap="wrap">
          {tiles.map((tile) => (
            <CountTile
              key={tile.title}
              label={tile.title}
              count={tile.count}
              note={tile.note}
              onClick={() => {
                onSelect(tile.title);
              }}
            />
          ))}
        </Stack>
      )}
      <Conclusion
        title="現在の所有者"
        detailedRows
        onShowPage={onShowPage}
        value={views.最終所有者}
        empty="該当する権利はありません。"
        emptyReasons={{
          所有権の登記なし: <EmptyValue label="所有権の登記なし" />,
          共用部分: <EmptyValue label="規約共用部分" />,
          敷地権: <EmptyValue label="敷地権（所有者は区分建物の登記で確認）" />,
        }}
      >
        {(owners, rows) => (
          <OwnerRows owners={owners} rows={rows} onShowPage={onShowPage} />
        )}
      </Conclusion>
      <Conclusion
        title="現在有効な担保権"
        detailedRows
        onShowPage={onShowPage}
        value={views.現在有効な担保権}
        empty="該当する権利はありません。"
        emptyReasons={{
          所有権の登記なし: <EmptyValue label="なし" note="所有権の登記なし" />,
          共用部分: <EmptyValue label="なし" note="規約共用部分" />,
        }}
      >
        {(securities, rows) => (
          <Stack spacing={1}>
            {securities.map((security, index) => (
              <SecurityCard
                key={index}
                entry={security}
                review={rows?.[index]}
                jointCatalogs={jointCatalogs}
                onOpenJointCatalog={onOpenJointCatalog}
                onShowPage={onShowPage}
              />
            ))}
          </Stack>
        )}
      </Conclusion>
      {(["現在の登記名義人", "受託者の現在名義"] as const).map((key) =>
        hasValue(views[key]) ? (
          <Conclusion
            onShowPage={onShowPage}
            key={key}
            title={key}
            value={views[key]}
            empty="該当する名義はありません。"
          >
            {(titles) => (
              <ReviewedParties
                values={titles}
                issues={asList(asRecord(views[key]).問題)}
                onShowPage={onShowPage}
                mapParty={(title) => {
                  const item = asRecord(title);
                  return { ...asRecord(item.名義), 役割: text(item.役割) };
                }}
              />
            )}
          </Conclusion>
        ) : null
      )}
      {asList(views.現在の用益権).map((usage, index) => (
        <Conclusion
          onShowPage={onShowPage}
          key={index}
          title="現在の用益権者"
          value={asRecord(usage).権利者}
          empty="該当する権利者はありません。"
        >
          {(parties) => (
            <ReviewedParties
              values={parties}
              issues={asList(asRecord(asRecord(usage).権利者).問題)}
              onShowPage={onShowPage}
            />
          )}
        </Conclusion>
      ))}
    </Stack>
  );
};
